import { Grid } from "@mui/material";
import dayjs from "dayjs";
import CustomSelect from "@/components/CustomSelect";
import ProfileViewer from "@/components/ProfileViewer";
import SideSheetDrawer from "@/components/SidesheetDrawer";
import Text from "@/components/Text";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
// Leaf import: the group-visit barrel loads the group-appointment barrel back and forms a cycle.
import GroupSlotPicker from "@/pages/app/provider/group-visit/components/GroupSlotPicker";
import {
  APPOINTMENT_MODE_LABEL,
  GROUP_APPOINTMENT_STATUS_LABEL,
  GROUP_RESCHEDULE_DRAWER_WIDTH,
  GROUP_RESCHEDULE_END_FORMAT,
  GROUP_RESCHEDULE_LABEL,
  GROUP_RESCHEDULE_SLOT_FORMAT,
} from "../group-appointment-constant";
import { GroupSessionDetail } from "../group-appointment-types";
import { useGroupSessionReschedule } from "../hooks/use-group-session-reschedule";

export interface GroupRescheduleDrawerProps {
  opened: boolean;
  onClose: () => void;
  detail: GroupSessionDetail;
  onRescheduled: () => void;
}

const formatCurrentSlot = ({ startTime, endTime }: GroupSessionDetail) => {
  const start = dayjs(startTime).format(GROUP_RESCHEDULE_SLOT_FORMAT);
  return endTime
    ? `${start} - ${dayjs(endTime).format(GROUP_RESCHEDULE_END_FORMAT)}`
    : start;
};

/**
 * Group counterpart of the individual Reschedule Appointment drawer: session details,
 * Location for in-person sessions, and the therapists' open slots.
 */
const GroupRescheduleDrawer = ({
  opened,
  onClose,
  detail,
  onRescheduled,
}: GroupRescheduleDrawerProps) => {
  const reschedule = useGroupSessionReschedule(detail, onRescheduled);

  return (
    <SideSheetDrawer
      open={opened}
      title={GROUP_RESCHEDULE_LABEL.title}
      onClose={onClose}
      w={GROUP_RESCHEDULE_DRAWER_WIDTH}
      withCloseIcon
      footerActions={
        <StyledButtonV2
          label={GROUP_RESCHEDULE_LABEL.save}
          variant="primary"
          disabled={!reschedule.canSave}
          onClick={reschedule.save}
        />
      }
    >
      <Grid container spacing={3} sx={{ p: 2 }}>
        <Grid size={6}>
          <ProfileViewer
            gap="16px"
            firstColW={4.5}
            secondColW={7.5}
            data={{
              [GROUP_RESCHEDULE_LABEL.groupName]: detail.groupName ?? "",
              [GROUP_RESCHEDULE_LABEL.therapist]: detail.therapistName ?? "",
              [GROUP_RESCHEDULE_LABEL.coFacilitator]:
                detail.coTherapistName ?? "",
              [GROUP_RESCHEDULE_LABEL.visitType]:
                APPOINTMENT_MODE_LABEL[detail.mode] ?? "",
            }}
          />
        </Grid>
        <Grid size={6}>
          <ProfileViewer
            gap="16px"
            firstColW={4.5}
            secondColW={7.5}
            data={{
              [GROUP_RESCHEDULE_LABEL.status]:
                GROUP_APPOINTMENT_STATUS_LABEL[detail.sessionStatus] ?? "",
              [GROUP_RESCHEDULE_LABEL.enrolled]: String(
                detail.enrolledPatients ?? detail.patients.length
              ),
              [GROUP_RESCHEDULE_LABEL.currentSlot]: detail.startTime
                ? formatCurrentSlot(detail)
                : "",
            }}
          />
        </Grid>

        {reschedule.isInPerson && (
          <Grid size={12}>
            <CustomSelect
              label={GROUP_RESCHEDULE_LABEL.location}
              required
              name="locationId"
              placeholder={GROUP_RESCHEDULE_LABEL.locationPlaceholder}
              options={reschedule.locationOptions}
              value={reschedule.locationId}
              onChange={reschedule.setLocationId}
            />
          </Grid>
        )}

        <Grid size={12}>
          <Text fontWeight={500} sx={{ mb: 1 }}>
            {GROUP_RESCHEDULE_LABEL.dateTime}
          </Text>
          {reschedule.canViewSlots ? (
            <GroupSlotPicker
              search={reschedule.slotSearch}
              selectedDate={reschedule.selectedDate}
              selectedStartTime={reschedule.selectedSlot?.startTime ?? null}
              onDateChange={reschedule.selectDate}
              onSlotSelect={reschedule.selectSlot}
            />
          ) : (
            <Text color="grey">
              {GROUP_RESCHEDULE_LABEL.selectLocationFirst}
            </Text>
          )}
        </Grid>
      </Grid>
    </SideSheetDrawer>
  );
};

export default GroupRescheduleDrawer;
